/**
 * The console's single door to the API.
 *
 * Every request goes through here so authentication, workspace scoping and error shape
 * are decided once. Nothing else in the console calls `fetch`.
 */

export interface ApiError extends Error {
  status: number;
  code: string;
}

function apiError(status: number, code: string, message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.name = 'ApiError';
  error.status = status;
  error.code = code;
  return error;
}

export interface Session {
  token: string;
  workspaceId: string;
}

let session: Session | null = null;

/**
 * Oturum reddedildiğinde çağrılır.
 *
 * Buradan haber vermek zorundayız çünkü `api` React'i tanımıyor: oturumu tutan taraf
 * AuthProvider ve süresi dolmuş bir jetonu temizlemesi gereken de o.
 */
let onRejected: (() => void) | null = null;

export function setSession(next: Session | null): void {
  session = next;
}

export function onSessionRejected(handler: (() => void) | null): void {
  onRejected = handler;
}

/**
 * Sunucu oturumu tazelediğinde çağrılır.
 *
 * Konsol kullanıldıkça jeton uzuyor; sabit bitiş, operatörü işin ortasında dışarı
 * atıyordu. Boşta kalan bir sekme yine ölüyor — yenilenme yalnız istek atıldıkça oluyor.
 */
let onRenewed: ((token: string) => void) | null = null;

export function onSessionRenewed(handler: ((token: string) => void) | null): void {
  onRenewed = handler;
}

/** Tazelenen jetonun döndüğü başlık; API tarafıyla aynı ad. */
const SESSION_HEADER = 'x-rabit-session-token';

export function currentSession(): Session | null {
  return session;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(session ? { authorization: `Bearer ${session.token}` } : {}),
      ...(session ? { 'x-workspace-id': session.workspaceId } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const taze = response.headers.get(SESSION_HEADER);
  if (taze && session) {
    // Sıradaki istek yeni jetonla çıksın diye ÖNCE burada değiştiriliyor; kalıcılık
    // AuthProvider'ın işi.
    session = { ...session, token: taze };
    onRenewed?.(taze);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  /**
   * Bir yanıt JSON OLMAYABİLİR ve bu normaldir.
   *
   * Deploy sırasında yönlendirici düz metin "no available server" döndürüyor; ayrıştırma
   * denemesi bunu `JSON Parse error: Unexpected identifier "no"` hâline getiriyordu —
   * yani geçici bir yeniden başlatma, kullanıcıya anlamsız bir hata olarak görünüyor ve
   * yaptığı işin bozulduğunu sandırıyordu.
   */
  let payload: unknown = null;
  let ayrisabildi = true;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      ayrisabildi = false;
    }
  }

  if (!response.ok) {
    if (!ayrisabildi) {
      // Sunucunun kendi metnini taşı; kısalt, çünkü bir HTML hata sayfası olabilir.
      throw apiError(
        response.status,
        'UNAVAILABLE',
        response.status >= 500
          ? 'Sunucu şu an yanıt vermiyor (büyük olasılıkla yeniden başlıyor). Birazdan tekrar dene.'
          : text.trim().slice(0, 200),
      );
    }
    const shape = (payload ?? {}) as { error?: { code?: string; message?: string } };
    /**
     * Süresi dolmuş oturum, sayfada gösterilecek bir hata DEĞİL.
     *
     * 401 hiç ele alınmıyordu: jeton (12 saat sonra) düştüğünde her kart "Invalid or
     * expired session token" yazıyor, kullanıcı sıkışıp kalıyor ve giriş ekranına dönüşün
     * tek yolu sekmeyi kapatmak oluyordu. Otomatik yenilenen bir sayfada bu, saniyede bir
     * tekrarlanan anlamsız bir hata anlamına geliyor.
     *
     * 403 BİLEREK dışarıda: o "giriş yapmadın" değil, "bu işe yetkin yok" demek ve
     * kullanıcıyı çıkışa atmak yanlış cevap olurdu.
     */
    if (response.status === 401 && session) {
      session = null;
      onRejected?.();
    }
    throw apiError(
      response.status,
      shape.error?.code ?? 'UNKNOWN',
      shape.error?.message ?? response.statusText,
    );
  }
  if (!ayrisabildi) {
    // Başarılı ama okunamayan bir gövde: sessizce `undefined` döndürmek, çağıranı
    // olmayan bir alanı okurken patlatır ve sebebi çok uzakta görünür.
    throw apiError(response.status, 'UNAVAILABLE', 'Sunucudan beklenmeyen bir yanıt geldi.');
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
